import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, FilterList } from "@mui/icons-material";
import close_icon from '../../../assets/icons/close.svg'
import moment from "moment";
import { ContentCopy } from "@mui/icons-material";
import toast from "react-hot-toast";
import {
  LocalShipping,
  AssignmentTurnedIn,
  Cancel,
  TwoWheeler,
  Inventory,
} from "@mui/icons-material";
import EmptyState from "../../../components/EmptyState";
import PaginationComponentOne from "../../../components/Pagination/Pagination-O";

const ActivityLogDrawer = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const {
    // allActivityLogs,
    allActivityLogsLoading,
    // totalActivityLogs,
  } = useSelector((state) => state.storeManagement);

  // Mock data for demonstration
  const mockAllActivityLogs = [
    {
      id: 1,
      activity_type: "STOCK_IN",
      activity_type_display: "Stock Received",
      timestamp: "2024-08-04T14:36:00Z",
      actor: { 
        name: "Grace Okoro", 
        username: "grace.okoro",
        role: "Store Manager" 
      },
      details: {
        quantity: 150,
        product_name: "Urea Fertilizer",
        variant_info: "50kg bags",
        location: "Kano Warehouse",
        status: "CONFIRMED"
      }
    },
    {
      id: 2,
      activity_type: "STOCK_TRANSFER-OUTGOING",
      activity_type_display: "Stock Transfer Outgoing",
      timestamp: "2024-08-04T12:15:00Z",
      actor: { 
        name: "John Adamu", 
        username: "john.adamu",
        role: "Inventory Manager" 
      },
      details: {
        quantity: 75,
        product_name: "Knapsack Sprayers",
        receiving_store: "Benin Store",
        status: "DISPATCHED"
      }
    },
    {
      id: 3,
      activity_type: "PURCHASE",
      activity_type_display: "Purchase Order",
      timestamp: "2024-08-04T10:22:00Z",
      actor: { 
        name: "Mary Johnson", 
        username: "mary.johnson",
        role: "Customer" 
      },
      details: {
        quantity: 25,
        product_name: "NPK Fertilizer",
        variant_info: "25kg bags",
        location: "Online Store",
        reference: "ORD-2024-001234",
        status: "PROCESSING"
      },
      deliveryType: "Express"
    },
    // Add more mock data
    ...Array.from({ length: 20 }, (_, i) => ({
      id: i + 4,
      activity_type: ["STOCK_IN", "STOCK_OUT", "PURCHASE", "LOW_STOCK", "STOCK_TRANSFER-INCOMING"][i % 5],
      activity_type_display: ["Stock Received", "Stock Distributed", "Purchase Order", "Low Stock Alert", "Stock Transfer Incoming"][i % 5],
      timestamp: moment().subtract(i, 'hours').toISOString(),
      actor: { 
        name: ["Grace Okoro", "John Adamu", "Mary Johnson", "System", "Ibrahim Musa"][i % 5],
        username: ["grace.okoro", "john.adamu", "mary.johnson", "system", "ibrahim.musa"][i % 5],
        role: ["Store Manager", "Inventory Manager", "Customer", null, "Warehouse Supervisor"][i % 5]
      },
      details: {
        quantity: Math.floor(Math.random() * 100) + 10,
        product_name: ["Urea Fertilizer", "NPK Fertilizer", "Herbicide", "Pesticide", "Knapsack Sprayers"][i % 5],
        variant_info: ["50kg bags", "25kg bags", "1L bottles", "500ml bottles", "Unit"][i % 5],
        location: ["Kano Warehouse", "Lagos Store", "Online Store", "Abuja Depot", "Jos Depot"][i % 5],
        status: ["CONFIRMED", "DISPATCHED", "PROCESSING", "PENDING", "DELIVERED"][i % 5],
        reference: i % 3 === 0 ? `ORD-2024-00${String(i + 1000).padStart(4, '0')}` : null,
        receiving_store: i % 4 === 0 ? ["Benin Store", "Port Harcourt Store", "Kaduna Store"][i % 3] : null,
        source_store: i % 5 === 0 ? ["Lagos Central Store", "Abuja Main Store", "Kano Hub"][i % 3] : null
      },
      deliveryType: i % 3 === 0 ? ["Express", "Standard", "Same Day"][i % 3] : null
    }))
  ];

  // Activity type filtering options
  const filterOptions = [
    { value: "ALL", label: "All Activities" },
    { value: "STOCK_IN", label: "Stock Received" },
    { value: "STOCK_OUT", label: "Stock Distributed" },
    { value: "STOCK_TRANSFER-INCOMING", label: "Incoming Transfers" },
    { value: "STOCK_TRANSFER-OUTGOING", label: "Outgoing Transfers" },
    { value: "PURCHASE", label: "Purchase Orders" },
    { value: "LOW_STOCK", label: "Low Stock Alerts" },
  ];

  // Helper functions (same as in StoreActivityLog)
  const getActivityTypeBadgeStyle = (activityType) => {
    const styles = {
      STOCK_CONFIRMATION: "border-[#2B8C34] text-[#2B8C34] bg-white",
      STOCK_REQUEST: "border-[#D4A407] text-[#D4A407] bg-white",
      LOW_STOCK: "border-[#D4A407] text-[#D4A407] bg-white",
      STOCK_IN: "border-[#2B8C34] text-[#2B8C34] bg-white",
      STOCK_OUT: "border-[#B92020] text-[#B92020] bg-white",
      OUT_OF_STOCK: "border-[#B92020] text-[#B92020] bg-white",
      BACK_IN_STOCK: "border-[#2B8C34] text-[#2B8C34] bg-white",
      NEW_PRODUCT: "border-[#6B46C1] text-[#6B46C1] bg-white",
      STOCK_TRANSFER: "border-[#0066CC] text-[#0066CC] bg-white",
      "STOCK_TRANSFER-INCOMING": "border-[#0066CC] text-[#0066CC] bg-white",
      "STOCK_TRANSFER-OUTGOING": "border-[#0066CC] text-[#0066CC] bg-white",
      PURCHASE: "border-[#D4A407] text-[#D4A407] bg-white",
    };
    return styles[activityType] || "border-[#5F6D60] text-[#5F6D60] bg-[#F5F5F5]";
  };

  const getActivityTypeDisplay = (activityType) => {
    const displays = {
      STOCK_CONFIRMATION: "Stock Confirmation",
      STOCK_REQUEST: "Stock Request", 
      LOW_STOCK: "Low Stock Alert",
      STOCK_IN: "Stock Received",
      STOCK_OUT: "Stock Distributed",
      OUT_OF_STOCK: "Out of Stock",
      BACK_IN_STOCK: "Back in Stock",
      NEW_PRODUCT: "New Product Added",
      STOCK_TRANSFER: "Stock Transfer",
      "STOCK_TRANSFER-INCOMING": "Stock Transfer Incoming",
      "STOCK_TRANSFER-OUTGOING": "Stock Transfer Outgoing",
      PURCHASE: "Purchase Order",
    };
    return displays[activityType] || activityType;
  };

  const getStatusBadgeStyle = (status) => {
    const styles = {
      DISPATCHED: "border-[#0066CC] text-[#0066CC] bg-white",
      DELIVERED: "border-[#2B8C34] text-[#2B8C34] bg-white",
      PROCESSING: "border-[#D4A407] text-[#D4A407] bg-white",
      COMPLETED: "border-[#2B8C34] text-[#2B8C34] bg-white",
      CANCELLED: "border-[#B92020] text-[#B92020] bg-white",
      PENDING: "border-[#D4A407] text-[#D4A407] bg-white",
      CONFIRMED: "border-[#2B8C34] text-[#2B8C34] bg-white",
      REQUESTED: "border-[#D4A407] text-[#D4A407] bg-white",
    };
    return styles[status] || "border-[#5F6D60] text-[#5F6D60] bg-[#F5F5F5]";
  };

  const getStatusIcon = (status) => {
    const iconProps = { style: { fontSize: 14 } };
    switch (status?.toUpperCase()) {
      case "DISPATCHED":
        return <LocalShipping {...iconProps} />;
      case "DELIVERED":
      case "COMPLETED":
      case "CONFIRMED":
        return <AssignmentTurnedIn {...iconProps} />;
      case "CANCELLED":
        return <Cancel {...iconProps} />;
      case "PROCESSING":
      case "PENDING":
      case "REQUESTED":
        return <Inventory {...iconProps} />;
      default:
        return null;
    }
  };

  const handleCopyNumber = (number) => {
    navigator.clipboard.writeText(number);
    toast.success(`${number} copied successfully`);
  };

  const capitalizeFirstLetter = (str) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase() || "";
  };

  // Filter and search logic
  const filteredLogs = mockAllActivityLogs.filter(log => {
    const matchesSearch = !searchTerm || 
      log.details?.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.actor?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details?.location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === "ALL" || log.activity_type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  // Pagination
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedLogs = filteredLogs.slice(startIndex, startIndex + pageSize);
  const totalItems = filteredLogs.length;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (isOpen) {
      // Reset pagination when drawer opens
      setCurrentPage(1);
      // Fetch all activity logs when drawer opens
      // dispatch(getAllStoreActivityLogs({ page: currentPage, page_size: pageSize, search: searchTerm, filter: filterType }));
    }
  }, [isOpen, searchTerm, filterType, dispatch]);

  return (
    <div className={`${!isOpen ? 'pointer-events-none' : ''}`}>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
          isOpen ? 'bg-opacity-50 pointer-events-auto' : 'bg-opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className={`fixed right-0 top-0 h-full w-[500px] bg-white shadow-lg z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-[#E1E6E1]">
          <h2 className="text-lg font-semibold text-[#5C715E]">All Activity Logs</h2>
            <button
                type="button"
                onClick={onClose}
                className="text-gray-400 bg-[#EDF7EE]
                              py-4 px-6
                            rounded-lg text-sm flex items-center justify-center"
              >
                <img src={close_icon} className="" alt="close icon" />
              </button>
        </div>

        {/* Search and Filter */}
        <div className="p-4 border-b border-[#E1E6E1] space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#5F6D60] w-4 h-4" />
            <input
              type="text"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#E1E6E1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B8C34] focus:border-transparent"
            />
          </div>

          {/* Filter */}
          <div className="relative">
            <FilterList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#5F6D60] w-4 h-4" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#E1E6E1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B8C34] focus:border-transparent"
            >
              {filterOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {allActivityLogsLoading ? (
            <div className="p-4 space-y-4">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="flex justify-between items-center mb-3 bg-gray-100 px-3 py-2 rounded">
                    <div className="h-6 bg-gray-200 rounded w-24"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                  <div className="space-y-2 px-3">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : paginatedLogs.length === 0 ? (
            <div className="p-8">
              <EmptyState text="No activity logs found" />
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {paginatedLogs.map((activity, index) => (
                <div
                  key={activity.id}
                  className="border border-[#E1E6E1] rounded-lg overflow-hidden"
                >
                  {/* Activity Type Badge and Timestamp */}
                  <div className="flex justify-between items-center bg-[#F6F7F6] px-3 py-2">
                    <span
                      className={`px-2 py-1 rounded border ${getActivityTypeBadgeStyle(
                        activity.activity_type
                      )} font-semibold text-xs`}
                    >
                      {activity.activity_type_display || getActivityTypeDisplay(activity.activity_type)}
                    </span>
                    <span className="text-xs text-[#5F6D60] font-semibold">
                      {moment(activity.timestamp).format("h:mm A")}{" "}
                      <span className="text-[#A1A8A1] mr-2">|</span>
                      {moment(activity.timestamp).format("DD MMM YY")}
                    </span>
                  </div>

                  {/* Activity Details */}
                  <div className="space-y-0">
                    {/* Unit Added/Transferred */}
                    {activity.details?.quantity && (
                      <div className="flex items-center border-t border-[#E1E6E1] py-2 px-3">
                        <span className="text-xs font-semibold text-[#5F6D60] mr-2">
                          {activity.activity_type === "STOCK_IN"
                            ? "Unit Added:"
                            : activity.activity_type.includes("TRANSFER")
                            ? "Unit Transferred:"
                            : activity.activity_type === "PURCHASE"
                            ? "Unit Bought:"
                            : "Quantity:"}
                        </span>
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded border ${
                            activity.activity_type === "STOCK_IN" ||
                            activity.activity_type === "STOCK_TRANSFER-INCOMING"
                              ? "text-[#2B8C34] border-[#2B8C34] bg-[#EDF7EE]"
                              : "text-[#B92020] border-[#B92020] bg-[#FEE2E2]"
                          }`}
                        >
                          {activity.activity_type === "STOCK_IN" ||
                          activity.activity_type === "STOCK_TRANSFER-INCOMING"
                            ? "+"
                            : "-"}
                          {activity.details.quantity}
                        </span>
                      </div>
                    )}

                    {/* Product Information */}
                    {activity.details?.product_name && (
                      <div className="flex items-center border-t border-[#E1E6E1] py-2 px-3">
                        <span className="text-xs text-[#5F6D60] font-semibold mr-2">Product:</span>
                        <div className="border border-[#E1E6E1] rounded py-1 px-2 w-fit text-[#5F6D60] font-semibold text-xs bg-white">
                          {activity.details.product_name}
                          {activity.details.variant_info && ` / ${activity.details.variant_info}`}
                        </div>
                      </div>
                    )}

                    {/* Actor Information */}
                    {activity.actor && activity.actor.name !== "System" && (
                      <div className="flex items-center border-t border-[#E1E6E1] py-2 px-3">
                        <span className="text-xs font-semibold text-[#5F6D60] mr-2">
                          {activity.activity_type === "STOCK_IN"
                            ? "Stock Added By:"
                            : activity.activity_type.includes("TRANSFER")
                            ? "Stock Transferred By:"
                            : activity.activity_type === "PURCHASE"
                            ? "Ordered By:"
                            : "Performed By:"}
                        </span>
                        <span className="text-xs font-semibold text-[#2E332F]">
                          {activity.actor.name || activity.actor.username}
                        </span>
                        {activity.actor.role && (
                          <span className="text-xs text-[#5F6D60] ml-2 bg-[#F6F7F6] font-semibold border border-[#E1E6E1] px-2 py-1 rounded">
                            {activity.actor.role}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Source Store for TRANSFER-INCOMING */}
                    {activity.activity_type === "STOCK_TRANSFER-INCOMING" &&
                      activity.details?.source_store && (
                        <div className="flex items-center border-t border-[#E1E6E1] py-2 px-3">
                          <span className="text-xs font-semibold text-[#5F6D60] mr-2">
                            Source Store:
                          </span>
                          <span className="text-xs text-[#5F6D60] font-semibold bg-[#F6F7F6] border border-[#E1E6E1] px-2 py-1 rounded">
                            {activity.details.source_store}
                          </span>
                        </div>
                      )}

                    {/* Receiving Store for TRANSFER-OUTGOING */}
                    {activity.activity_type === "STOCK_TRANSFER-OUTGOING" &&
                      activity.details?.receiving_store && (
                        <div className="flex items-center border-t border-[#E1E6E1] py-2 px-3">
                          <span className="text-xs font-semibold text-[#5F6D60] mr-2">
                            Receiving Store:
                          </span>
                          <span className="text-xs text-[#5F6D60] font-semibold bg-[#F6F7F6] border border-[#E1E6E1] px-2 py-1 rounded">
                            {activity.details.receiving_store}
                          </span>
                        </div>
                      )}

                    {/* Location/Store Information for other activity types */}
                    {!activity.activity_type.includes("TRANSFER") &&
                      activity.details?.location && (
                        <div className="items-center border-t border-[#E1E6E1] flex justify-between pr-2 py-2 px-3">
                          <div className="flex items-center">
                            <span className="text-xs font-semibold text-[#5F6D60] mr-2">
                              {activity.activity_type === "PURCHASE"
                                ? "Channel:"
                                : "Location:"}
                            </span>

                            <span
                              className={`text-xs font-semibold ${
                                activity.activity_type === "PURCHASE"
                                  ? "text-[#5F6D60] bg-[#F6F7F6] border border-[#E1E6E1] px-2 py-1 rounded"
                                  : "text-[#2E332F]"
                              }`}
                            >
                              {activity.details.location}
                            </span>
                          </div>

                          {activity.activity_type === "PURCHASE" && activity.deliveryType && (
                            <span className="text-xs text-[#5F6D60] font-semibold bg-[#F6F7F6] border border-[#E1E6E1] px-2 py-1 rounded flex items-center gap-1 ml-2">
                              <TwoWheeler
                                className="text-[#5F6D60] cursor-pointer"
                                style={{ fontSize: "12px" }}
                              />
                              {activity.deliveryType}
                            </span>
                          )}
                        </div>
                      )}

                    {/* Order ID for purchases */}
                    {activity.details?.reference &&
                      activity.activity_type === "PURCHASE" && (
                        <div className="flex items-center border-t border-[#E1E6E1] py-2 px-3">
                          <span className="text-xs font-semibold text-[#5F6D60] mr-2">
                            Order ID:
                          </span>
                          <div className="border border-[#E1E6E1] px-1 py-1 rounded bg-white w-fit gap-0.5 flex items-center text-xs text-[#5F6D60] font-semibold">
                            {activity.details.reference}
                            <ContentCopy
                              className="text-[#5F6D60] cursor-pointer"
                              style={{ fontSize: "10px" }}
                              onClick={() =>
                                handleCopyNumber(activity.details.reference)
                              }
                            />
                          </div>
                        </div>
                      )}

                    {/* Status with Icon */}
                    {activity.details?.status && (
                      <div className="flex items-center border-t border-[#E1E6E1] py-2 px-3">
                        <span className="text-xs font-semibold text-[#5F6D60] mr-2">Status:</span>
                        <div className="flex items-center">
                          <span
                            className={`px-2 py-1 rounded border ${getStatusBadgeStyle(
                              activity.details.status
                            )} font-semibold text-xs flex items-center gap-1`}
                          >
                            {getStatusIcon(activity.details.status)}
                            {capitalizeFirstLetter(activity.details.status)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!allActivityLogsLoading && paginatedLogs.length > 0 && (
          <div className="border-t border-[#E1E6E1] p-4">
            <PaginationComponentOne
              totalItems={totalItems}
              page={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogDrawer;
